import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { ResultItem, ResultsetItem } from '@models/resultset-item';
import { SimulationModel } from '@models/simulation-model';
import { AppConfigProvider } from '@root/app/app.config.provider';
import { OpenIdConnectService } from '@services/auth/open-id-connect.service';
import { NotificationsService } from '@services/notifications/notifications.service';
import { ApplicationStatus } from '@services/results/application-status';
import { ResultsStoreService } from '@services/results/results-store.service';
import { ResultsService } from '@services/results/results.service';
import { TrackingService } from '@services/tracking/tracking.service';
import { Toast } from 'ngx-toastr';
import { cloneDeep } from 'lodash-es';
import { BehaviorSubject, Observable, of } from 'rxjs';
import { catchError, tap } from 'rxjs/operators';
import { v4 as uuidv4 } from 'uuid';
import { GlobalParamsService } from '../global-params/global-params.service';
import { ToastLevel } from '@shared/toasterSetup';

export interface SimulationProgress {
  total?: number;
  completed?: number;
  perc?: number;
  isRunning?: boolean;
}

interface ParamGetRunID {
  scenarioID: number;
  datasetID: number;
  parentRunID?: string;
}

@Injectable({ providedIn: 'root' })
export class SimulationRunnerService {
  public rerunningSimulation = false;
  public rerunId: string;
  private appStatus: ApplicationStatus;

  public progress$: BehaviorSubject<SimulationProgress> = new BehaviorSubject<SimulationProgress>({
    completed: 0,
    total: 0,
    isRunning: false,
  });

  constructor(
    private auth: OpenIdConnectService,
    private http: HttpClient,
    private resultsStore: ResultsStoreService,
    private trackService: TrackingService,
    private resultsService: ResultsService,
    private notifications: NotificationsService,
    private globalParams: GlobalParamsService,
    private appConfig: AppConfigProvider
  ) {
    this.resultsStore.appStatus$.subscribe((status: ApplicationStatus) => {
      this.appStatus = status;

      switch (status) {
        case ApplicationStatus.DoneSimulation:
          // Make sure bar is at 100%
          const total = this.progress$.getValue().total;
          this.updateProgress({ completed: total });
          break;
      }
    });
  }

  public getNewUUID() {
    return uuidv4();
  }

  private getPreviousRunFromIdentifiers(identifiers: ResultsetItem[], runToFind: ParamGetRunID): ResultsetItem {
    const found = identifiers.find(rs => {
      return runToFind.parentRunID
        ? rs.DataSetID === runToFind.datasetID && rs.Scenario_ID === runToFind.scenarioID && runToFind.parentRunID === rs.RunID_Used
        : rs.DataSetID === runToFind.datasetID && rs.Scenario_ID === runToFind.scenarioID;
    });
    return found || null;
  }

  /**
   * Obtains the state from the application and maps it to the excepted model for the API
   * It will also filter the stresses out on a rerun
   */
  private prepareDataForAPI(state: any[]) {
    const identifiers = this.resultsService.resultSetIdentifiers$.getValue();

    return state.map((resultset: ResultsetItem) => {
      const previousRun = this.getPreviousRunFromIdentifiers(identifiers, {
        scenarioID: resultset.Scenario_ID,
        datasetID: resultset.DataSetID,
        parentRunID: resultset.Is_Stress ? resultset.RunID_Used : null,
      });
      const baselineRunID = previousRun && previousRun.RunID ? previousRun.RunID : this.getNewUUID();

      resultset.IgnoreErrors = false;
      const model: SimulationModel = {
        Description: resultset.Description,
        Mnemonic: resultset.Mnemonic,
        Scenario_ID: resultset.Scenario_ID,
        ScenarioID: resultset.Scenario_ID,
        ScenarioMnemonic: resultset.Scenario_Mnemonic,
        DataSetID: resultset.DataSetID,
        ResultsetID: previousRun && previousRun.ResultSetID ? previousRun.ResultSetID : resultset.ResultSetID || -1,
        UserID: this.auth.userId,
        Is_Stress: resultset.Is_Stress ? resultset.Is_Stress : false, // Baseline by default
        // It will be true for reruns, and his default value for runs. False as fallback for undefined or null values
        IsTemp: this.rerunningSimulation || resultset.IsTemp || false,
        RunID: baselineRunID,
        RunID_Used: resultset.Is_Stress ? resultset.RunID_Used : null,
        IgnoreErrors: false,
        Order: resultset.Order,
      };
      // In case of a rerun of a simulation, reruns only the baseline but not its stresses
      if (!resultset.Is_Stress && !this.rerunningSimulation) {
        model.StressList = resultset.StressList.map(stress => {
          const previousStressRun = this.getPreviousRunFromIdentifiers(identifiers, {
            scenarioID: stress.Scenario_ID,
            datasetID: stress.DataSetID,
            parentRunID: baselineRunID,
          });
          const stressRunID = previousStressRun && previousStressRun.RunID ? previousStressRun.RunID : this.getNewUUID();

          const mappedStress = {
            Description: stress.Description,
            Mnemonic: stress.Mnemonic,
            Scenario_ID: stress.Scenario_ID,
            ScenarioID: stress.Scenario_ID,
            ScenarioMnemonic: stress.Scenario_Mnemonic,
            DataSetID: stress.DataSetID,
            ResultsetID: previousStressRun && previousStressRun.RunID ? previousStressRun.ResultSetID : stress.ResultSetID || -1,
            UserID: this.auth.userId,
            IsTemp: true,
            RunID: stressRunID,
            RunID_Used: baselineRunID,
            Is_Stress: true,
            IgnoreErrors: false,
            Order: stress.Order,
          };
          this.resultsStore.updateByUUID(
            stressRunID,
            {
              IsLoading: true,
              Hidden: false,
              Mnemonic: mappedStress.Mnemonic,
              Description: mappedStress.Description,
              DataSetID: mappedStress.DataSetID,
              Scenario_ID: mappedStress.Scenario_ID,
              Is_Stress: true,
              Scenario_Mnemonic: mappedStress.ScenarioMnemonic,
              IgnoreErrors: false,
              ResultsetID: mappedStress.ResultsetID,
              Order: mappedStress.Order,
            },
            {
              ...mappedStress,
              // Property overwritting
              id: stressRunID,

              IsLoading: true,
              Parent: resultset.id,
              Is_Stress: true,
              Hidden: false,
            }
          );
          return mappedStress;
        });
      }

      this.resultsStore.updateByUUID(
        baselineRunID,
        {
          IsLoading: true,
          Hidden: false,
          Mnemonic: model.Mnemonic,
          Description: model.Description,
          DataSetID: model.DataSetID,
          Scenario_ID: model.Scenario_ID,
          Scenario_Mnemonic: model.ScenarioMnemonic,
          IgnoreErrors: false,
          ResultsetID: model.ResultsetID,
          Order: model.Order,
        },
        {
          ...model,
          // Property overwritting
          id: baselineRunID,
          IsLoading: true,
          Hidden: false,
        }
      );
      return model;
    });
  }

  public calculate(simulation: SimulationModel): Promise<ResultItem[]> {
    let environment;
    this.appConfig.environment.subscribe(env => {
      environment = env.API.CALCAPI;
    });
    const calcUrl = `${environment}/api/calculation/run`;
    const results: Promise<ResultItem[]> = this.http
      .post<ResultItem[]>(calcUrl, simulation)
      .pipe(
        tap(_ => {
          this.trackService.updateModelDataChangesFlags('resultsets', true, `calculation ${simulation}`);
        }),
        catchError((res: Observable<ResultItem[]>) => {
          console.error(`💀 Simulation failed`, { simulation });
          this.resultsStore.appStatus$.next(ApplicationStatus.DoneSimulation);

          this.handleCalculationException(simulation.Mnemonic, simulation.RunID, simulation);
          // return res;
          return of(<ResultItem[]>[]);
        })
      )
      .toPromise();

    return results;
  }

  public rerunSimulation(tabMnemonic: string) {
    const targetFound = this.resultsStore.resultsList.find(restultset => restultset.Mnemonic === tabMnemonic);
    if (targetFound) {
      this.rerunningSimulation = true;
      this.rerunId = targetFound.RunID;
      const simulationsToRun = [targetFound];
      this.runSimulation(simulationsToRun);
    } else {
      console.error(
        `You are trying to rerun a simulation for the scenario ${tabMnemonic},
        but this mnemonic no longer exists in the system.`
      );
    }
  }

  public runSimulation(data: (SimulationModel | ResultsetItem)[]): void {
    // changes app status
    this.resultsStore.appStatus$.next(ApplicationStatus.RunningSimulation);

    // Prepare the data for running
    let countDone = 0;
    const preparedData = this.prepareDataForAPI(data);

    // Calculates the number of simulations we're running
    let dataSize = 0;
    preparedData.forEach((baseline: SimulationModel | ResultsetItem | any) => {
      dataSize++;
      if (baseline.StressList) {
        dataSize += baseline.StressList.length;
      }
    });

    this.updateProgress({ completed: countDone, total: dataSize, isRunning: true });

    // Runs the simulation
    preparedData.forEach(resultset => {
      try {
        // Avoid using resultset reference value, in case it's already been modified in the store.
        const resultItemToProcess = cloneDeep(resultset);

        // For each baseline, fetch the calculation from API
        this.calculate(resultItemToProcess)
          .then(calculationResult => {
            // If result is 200 but there's an error during the calculations, treat it as exception:
            if (calculationResult[0].DataLoadError === true) {
              // Show a toast for the failed baseline
              this.handleCalculationException(calculationResult[0].Mnemonic, resultItemToProcess.RunID, resultItemToProcess);
              return;
            }
            this.resultsStore.calculationResult$.next(calculationResult[0]);

            // Push the results to the App state
            this.parseResultsIntoResultsetType(resultItemToProcess, calculationResult);
            if (calculationResult) {
              countDone++;
              this.incrementProgress();

              // Send the DoneSimulation status if we've completed the full array of simulations
              if (countDone === dataSize) {
                this.resultsStore.appStatus$.next(ApplicationStatus.DoneSimulation);
              }
            }

            if (resultItemToProcess.StressList) {
              // Runs each of the stresses for this baseline
              resultItemToProcess.StressList.forEach(stress => {
                // Avoid using stress reference value, in case it's already been modified in the store.
                const stressToProcess = { ...stress };

                this.calculate(stressToProcess).then(stressResult => {
                  // If result is 200 but there's an error during the calculations, treat it as exception:
                  if (stressResult && stressResult[0].DataLoadError === true) {
                    // this.handleCalculationException(stressResult[0].Mnemonic, stress.RunID);
                    this.handleCalculationException(stressResult[0].Mnemonic, stressToProcess.RunID, stressToProcess);
                    return;
                  }

                  this.resultsStore.calculationResult$.next(stressResult[0]);
                  // And push to the state
                  this.parseResultsIntoResultsetType(stressToProcess, stressResult);
                  if (stressResult) {
                    countDone++;
                    this.incrementProgress();
                    // Send the DoneSimulation status if we've completed the full array of simulations
                    if (countDone === dataSize) {
                      // Emit the number of periods of every result (on run) for Fix 4795
                      try {
                        if (this.resultsStore.resultsList && this.resultsStore.resultsList.length > 0) {
                          const ranNumPeriodsList = this.resultsStore.resultsList.map(result => result.Results.PeriodDates.length - 1);
                          // Needs to update the number of periods in the globalParamsService before proceed
                          this.globalParams
                            .getParams()
                            .toPromise()
                            .then(() => this.resultsStore.numPeriodsOfRanSimulations$.next(ranNumPeriodsList));
                        }
                      } catch (err) {
                        console.warn('Error retrieving the number of periods for every result while running simulation');
                      }
                      this.resultsStore.appStatus$.next(ApplicationStatus.DoneSimulation);
                    }
                  }

                  if (countDone === dataSize) {
                    this.resultsService.clearResultSetIdentifiers();
                  }
                });
              });
            }
          })
          .catch(err => {
            console.error(`(2) Exception when trying to run a baseline calculation \n Stacktrace:\n${err}`);
            throw err;
          });
      } catch (err) {
        this.handleCalculationException(resultset.Mnemonic, resultset.RunID, resultset);
        console.error(`(1) Exception when trying to run a baseline calculation \n Stacktrace:\n${err}`);
      }
    });
  }

  /**
   * When a calculation does not finish, call this method to remove the resultset from the store if empty
   * or restore the resultset to the previous state if rerun. Then show a toast with the error.
   * If it's a baseline, repeat the operation for its stresses
   * @param mnemonic Mnemonic of the simulation that failed
   * @param runId Run ID of the simulation that failed
   * @param resultItem The resultset item in the store, if exists
   * @param toastContent A Toast object if we want to override the error message
   */
  private handleCalculationException(mnemonic: string, runId: string, resultItem?: SimulationModel, toastContent?: Toast) {
    // Verify if we have results from a previous run
    const resultsetInStore = this.resultsStore.resultsList.find(resultset => runId === resultset.RunID);
    // If we don't, remove the baseline from the Store
    // if we do, the store won't be modified and the previous result will be still the status quo
    if (resultsetInStore && resultsetInStore.Results === undefined) {
      this.resultsStore.remove(resultsetInStore);
    } else {
      this.resultsStore.updateByUUID(runId, { IsLoading: false });
    }

    if (resultItem && resultItem.StressList) {
      // Repeat the process above for all the stresses that depend on this baseline
      resultItem.StressList.forEach(stress => {
        const stressInStore = this.resultsStore.resultsList.find(resultset => stress.RunID === resultset.RunID);
        this.resultsStore.updateByUUID(stress.RunID, { IsLoading: false });
        if (stressInStore && stressInStore.Results === undefined) {
          this.resultsStore.remove(stressInStore);
        }
      });
    }

    // Show the toast
    const defaultToastMessage: Toast = {
      
      title: 'Simulation Failure!',
      message: `The simulation service failed to load the data to run simulation(${mnemonic}).`,
    } as Toast;
    this.notifications.showToast(ToastLevel.Error, toastContent || defaultToastMessage);

    this.updateProgressAfterException(resultItem);
  }

  public parseResultsIntoResultsetType(simulationInfo = {}, calculationResult) {
    try {
      const localizedDate = calculationResult.ResultRunDate ? `${calculationResult.ResultRunDate} UTC` : new Date();
      calculationResult.forEach(result => {
        const mergedSimulationDetailsAndResults = {
          ...simulationInfo,
          ...result,
          Run_Date: localizedDate,
        };
        if (result.RunID) {
          this.resultsStore.updateByUUID(
            result.RunID,
            {
              IsLoading: false,
              Results: result.Results,
              ResultSetID: simulationInfo['ResultSetID'] || simulationInfo['ResultsetID'] || -1,
              TempResultSetID: result.TempResultSetID,
              IsTemp: true,
              Run_Date: localizedDate,
              InMemory: true,
            },
            mergedSimulationDetailsAndResults
          );
        }
      });
      return calculationResult;
    } catch (err) {
      console.error(
        // tslint:disable-next-line:max-line-length
        `API Error in parseResultsIntoResultsetType() of the simulationRunnerService. calculationResult is\n`,
        calculationResult,
        `\nERROR:\n`,
        err
      );
    }
  }

  // Progress Bar Handling

  private incrementProgress() {
    const currentProgress: SimulationProgress = this.progress$.getValue();
    this.updateProgress({ completed: currentProgress.completed + 1 });
  }

  private updateProgress(progress: SimulationProgress) {
    const currentProgress: SimulationProgress = this.progress$.getValue();
    const newProgress = { ...currentProgress, ...progress };
    newProgress.perc = Math.floor((newProgress.completed * 100) / newProgress.total);

    if (
      (this.appStatus === ApplicationStatus.RunningSimulation && newProgress.isRunning && newProgress.completed >= newProgress.total) ||
      newProgress.perc >= 100
    ) {
      setTimeout(() => {
        setTimeout(() => {
          // Fallback to finish progress 100% reached over 5 seconds
          this.updateProgress({ isRunning: false });
        }, 5000);
      }, 500);
    }

    if (JSON.stringify(newProgress) !== JSON.stringify(currentProgress)) {
      // Only update progress if it changed
      this.progress$.next(newProgress);
    }
  }

  private updateProgressAfterException(resultItem: SimulationModel) {
    // Handle the Progress update after a calculation failed
    this.incrementProgress();
    const previousProgress = this.progress$.getValue();
    const previousTotal = previousProgress.total;

    let total = previousTotal;
    if (resultItem && resultItem.StressList) {
      total -= resultItem.StressList.length;
    }

    this.updateProgress({ total });
  }
}
